/**
 * knowledgeGraphStyle — pure, dependency-free styling + layout helpers for the
 * knowledge graph canvases (2026-07-24). Kept SEPARATE from
 * _KnowledgeSigmaCanvas so these can be unit-tested without importing `sigma`
 * (whose module top references WebGL2RenderingContext, undefined in jsdom).
 *
 * See the styling guide: docs/20260724/20260724_KNOWLEDGE_GRAPH_VIZ_STYLING_GUIDE.md
 */

// Tab10 palette — SAME values + SAME hash as the react-force-graph canvases so
// a label group gets an identical color across the 2D (Sigma) and 3D views.
// Used when the graph has real semantic groups (CORTEX entities, OBSIDIAN
// Page/Orphan, pillared MIND).
const _PALETTE = ['#60a5fa', '#f97316', '#a78bfa', '#34d399', '#f472b6', '#facc15', '#fb7185'];

export function colorForGroup(group: string): string {
  let h = 0;
  for (let i = 0; i < group.length; i++) {
    h = (h * 31 + group.charCodeAt(i)) >>> 0;
  }
  return _PALETTE[h % _PALETTE.length];
}

// Community palette — the GameMagick Desktop **Star Trek TOS "gumball lights"**
// (the glowing bridge indicator lamps: gold / red / blue / green, from
// gamemagick-desktop/src/settings/themes.flavors.css) EXTENDED into a wider
// vivid "candy" set so a many-community graph still gets "way more colors"
// while keeping the punchy TOS register. The four gumball hues lead so the
// largest communities read as the iconic TOS colors; the rest are saturated
// complements that pop on a black canvas. Cluster colors are meaningful (same
// color = mutually similar docs), not decorative.
export const COMMUNITY_PALETTE: string[] = [
  '#f2c300', // TOS gumball gold
  '#ff5a4a', // TOS gumball red
  '#0099f6', // TOS gumball blue
  '#55c078', // TOS gumball green
  '#ff8c42', // orange
  '#c964e0', // magenta-violet
  '#16d0c4', // teal-cyan
  '#ff6fb5', // hot pink
  '#a3e635', // lime
  '#7c8cff', // periwinkle
  '#ffd23f', // bright yellow
  '#ef4d78', // rose
  '#38bdf8', // sky
  '#f97316', // deep orange
  '#4ade80', // spring green
  '#e879f9', // orchid
];

// Map a community index (0-based) to a stable color. Negative/large indices
// wrap into range (defensive — Louvain yields small non-negative ints).
export function colorForCommunity(index: number): string {
  const L = COMMUNITY_PALETTE.length;
  return COMMUNITY_PALETTE[((Math.trunc(index) % L) + L) % L];
}

// Translucent "bubble" node fill (2026-07-24) — the 2D nodes read as slightly
// transparent glass so overlapping dots show through each other instead of
// flat opaque discs. Converts a #rrggbb palette color to rgba() with alpha;
// passes non-hex through unchanged (defensive).
export const NODE_ALPHA = 0.8;

export function withAlpha(color: string, alpha: number): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(color);
  if (!m) return color;
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

// Color a graph by GROUP only when it has real semantic variety; otherwise the
// caller colors by detected community. Threshold 3 keeps CORTEX (Person/…),
// OBSIDIAN (Page/Orphan) and pillared MIND on stable semantic colors while the
// unpilled-MIND "everything is Document" case falls through to communities.
export function shouldColorByCommunity(distinctGroupCount: number): boolean {
  return distinctGroupCount < 3;
}

// Node size from rendered edge degree. Edges are hidden by default (they
// white-out at 51k), so NODES are the whole visual — hubs must read clearly.
// sqrt keeps the long tail small while hubs grow; clamped [2, 16] so a leaf is
// a fine dot and the biggest hub is prominent but not a blob. (Sigma's
// adjustSizes layout option repels using these radii → less overlap.)
export function sizeForDegree(degree: number): number {
  return Math.max(2, Math.min(16, 2 + Math.sqrt(Math.max(0, degree)) * 1.6));
}

// ---------------------------------------------------------------------------
// Graph Explorer controls (2026-07-24) — strength filter + collide spacing.
// Pure so they're unit-testable without WebGL. Used identically by the 2D
// (Sigma) and 3D (three.js) canvases so the two surfaces filter/size the same.
// ---------------------------------------------------------------------------

// Default min-strength: start with a SMALL, high-value map (only the strongest
// links). The operator drags the slider DOWN to reveal weaker links, or flips
// "show all" to bypass the filter entirely.
export const DEFAULT_MIN_STRENGTH = 0.9;

// Default collide buffer (extra gap beyond each node's radius). 0 = touch-allowed.
export const DEFAULT_SPACING = 4;

// An edge's similarity weight, defaulting to 1 (always-visible) when absent —
// CORTEX/OBSIDIAN edges carry no weight and must never be filtered out.
export function edgeWeightOf(edge: { weight?: number | null }): number {
  return typeof edge.weight === 'number' && Number.isFinite(edge.weight) ? edge.weight : 1;
}

// Is an edge visible at the current strength filter? showAll bypasses it.
export function edgePassesStrength(
  weight: number,
  minStrength: number,
  showAll: boolean,
): boolean {
  return showAll || weight >= minStrength;
}

// Per-node MAX incident edge weight — a node is worth showing at strength S if
// it has at least one link of weight >= S. Nodes with no incident edge map to
// -Infinity (never pass a positive threshold; our edge-driven MIND graph has no
// such nodes, but CORTEX synthesized endpoints could).
export function nodeMaxWeightMap(
  nodes: readonly { id: string }[],
  edges: readonly { source: string; target: string; weight?: number | null }[],
): Map<string, number> {
  const m = new Map<string, number>();
  for (const n of nodes) m.set(n.id, -Infinity);
  for (const e of edges) {
    const w = edgeWeightOf(e);
    if (m.has(e.source) && w > (m.get(e.source) as number)) m.set(e.source, w);
    if (m.has(e.target) && w > (m.get(e.target) as number)) m.set(e.target, w);
  }
  return m;
}

// A node is visible if show-all, or its strongest link clears the threshold.
export function nodePassesStrength(
  maxWeight: number | undefined,
  minStrength: number,
  showAll: boolean,
): boolean {
  if (showAll) return true;
  return maxWeight !== undefined && maxWeight >= minStrength;
}

// Visible node/edge counts at a given filter — for the control-panel readout.
export function visibleCounts(
  nodes: readonly { id: string }[],
  edges: readonly { source: string; target: string; weight?: number | null }[],
  minStrength: number,
  showAll: boolean,
): { nodes: number; edges: number } {
  if (showAll) return { nodes: nodes.length, edges: edges.length };
  const maxW = nodeMaxWeightMap(nodes, edges);
  let nodeCount = 0;
  for (const n of nodes) if (nodePassesStrength(maxW.get(n.id), minStrength, showAll)) nodeCount++;
  let edgeCount = 0;
  for (const e of edges) if (edgePassesStrength(edgeWeightOf(e), minStrength, showAll)) edgeCount++;
  return { nodes: nodeCount, edges: edgeCount };
}

// react-force-graph (3D canvas) MUTATES a link's source/target from the id
// string into the resolved node object once the graph is processed. Read the id
// back out of either shape so the strength/focus filters work before AND after
// that mutation. Returns '' for a malformed endpoint (never matches an id).
export function linkEndpointId(end: unknown): string {
  if (typeof end === 'string') return end;
  if (end && typeof end === 'object' && typeof (end as { id?: unknown }).id === 'string') {
    return (end as { id: string }).id;
  }
  return '';
}

// Collide radius for the spacing relaxation: a node's draw radius plus the
// buffer-zone gap. Two nodes end up at least (r_a + r_b) apart → no overlap.
export function collideRadiusFor(nodeSize: number, spacing: number): number {
  return Math.max(1, nodeSize) + Math.max(0, spacing);
}

// Edge color, DENSITY-AWARE. Edges white-out from additive overlap: N
// semi-transparent lines stacked per pixel accumulate toward white. The alpha
// must therefore scale INVERSELY with edge count — a fixed alpha that reads as a
// faint haze on the 51k-edge MIND map would be near-invisible on a 200-edge
// CORTEX graph, and one tuned for CORTEX whites-out MIND. The UMAP semantic map
// packs nodes tightly (more overlap per pixel than the old spread force layout),
// which is why the map needs the faintest tier. Dark cool tone (110,130,180) so
// any residual accumulation is a blue-gray haze, never a white sheet.
export function edgeAlphaForCount(edgeCount: number): number {
  if (edgeCount <= 200) return 0.5; // sparse (CORTEX/OBSIDIAN) — clear lines
  if (edgeCount <= 2000) return 0.18;
  if (edgeCount <= 20000) return 0.05;
  return 0.015; // dense complete MIND map (~51k) — faint web, islands dominate
}

export function edgeColorForCount(edgeCount: number): string {
  return `rgba(110,130,180,${edgeAlphaForCount(edgeCount)})`;
}

// Fixed (precomputed) layout detection. The MIND graph can ship server-computed
// UMAP coordinates (x, y) per node — a SEMANTIC map where distance means
// (dis)similarity, built by services.mind_coord_builder. When EVERY node carries
// a finite (x, y), the canvas plots those fixed positions and SKIPS ForceAtlas2
// entirely (no worker, no ring/donut, instant render — layout is meaningful, not
// a force equilibrium). A single missing coord falls back to the force layout:
// partial coords would strand the un-placed nodes at the origin, so it is
// all-or-nothing. CORTEX/OBSIDIAN never send coords → always force layout,
// unchanged. An empty graph is not "fixed" (nothing to place).
export interface MaybePositionedNode {
  x?: number | null;
  y?: number | null;
}

export function hasFixedLayout(nodes: readonly MaybePositionedNode[]): boolean {
  if (nodes.length === 0) return false;
  return nodes.every(
    (n) =>
      typeof n.x === 'number' &&
      Number.isFinite(n.x) &&
      typeof n.y === 'number' &&
      Number.isFinite(n.y),
  );
}

// ForceAtlas2 iterations — used only by the SYNCHRONOUS fallback path (when the
// off-main-thread worker can't be spawned). LinLog needs a floor to separate
// clusters, but a big-graph sync run of ~150 iters is a ~7s main-thread freeze,
// so the fallback caps big graphs low (40) — a rough layout beats a frozen tab;
// the worker is the normal path. Empty graph → 0.
export function iterationsFor(order: number): number {
  if (order <= 0) return 0;
  if (order > 2000) return 40; // sync FALLBACK only: bound the freeze
  return Math.max(120, Math.min(400, Math.round(60000 / order)));
}

// How long (ms) to run the off-main-thread ForceAtlas2 worker before stopping
// it (the layout settles, then we stop to free the CPU). Scales with size: a
// 12k-node linLog layout needs several seconds of worker time to spread; small
// graphs settle fast. Empty graph → 0.
export function layoutDurationMsFor(order: number): number {
  if (order <= 0) return 0;
  if (order > 6000) return 7000;
  if (order > 2000) return 4500;
  return 2000;
}

// ForceAtlas2 settings tuned to SPREAD dense clusters (the "larger distance
// between gang-nodes" ask) instead of packing them into a ball. See guide §2.
export interface ForceSettings {
  barnesHutOptimize: boolean;
  barnesHutTheta: number;
  linLogMode: boolean;
  outboundAttractionDistribution: boolean;
  adjustSizes: boolean;
  strongGravityMode: boolean;
  gravity: number;
  scalingRatio: number;
  slowDown: number;
}

export function forceSettingsFor(order: number): ForceSettings {
  return {
    barnesHutOptimize: order > 2000, // O(n log n) — required for 12k nodes
    barnesHutTheta: 0.6,
    // FLATTEN into a filled disc (no donut hole). A large similarity graph
    // shells into a RING under plain FA2: ordinary gravity balances the mass's
    // mutual repulsion at a radius, leaving the center empty (linLog off does
    // NOT fix this). strongGravityMode makes gravity ∝ distance-from-center, so
    // far nodes are pulled hard inward → the center FILLS. linLog +
    // outboundAttractionDistribution (both extra ring causes) stay OFF.
    // Community COLOR conveys clusters; strong gravity gives the flat map.
    linLogMode: false,
    outboundAttractionDistribution: false,
    strongGravityMode: true, // gravity ∝ distance → fills the center, no ring
    adjustSizes: true, // repel using node radius → less overlap
    gravity: 1.0, // moderate; strong mode already amplifies it
    scalingRatio: 12, // internal repulsion for spacing against the strong pull
    slowDown: 6, // keep the compacted field stable
  };
}

// ---------------------------------------------------------------------------
// Node data-card model (rule 4). Pure so it is unit-testable without WebGL /
// Sigma; the canvas gathers `neighbors` from the graphology graph and passes
// the raw attrs in. Everything the card renders is computed here.
// ---------------------------------------------------------------------------
export interface NodeCardAttrs {
  id: string;
  name: string;
  cluster: string; // group name OR "Cluster N" for community coloring
  degree: number;
}

export interface NodeCardModel {
  id: string;
  name: string;
  cluster: string;
  degree: number;
  neighborCount: number;
  topNeighbors: string[]; // display names, capped
}

export function nodeCardModel(
  attrs: NodeCardAttrs,
  neighborNames: string[],
  maxNeighbors = 6,
): NodeCardModel {
  return {
    id: attrs.id,
    name: attrs.name || attrs.id,
    cluster: attrs.cluster,
    degree: attrs.degree,
    neighborCount: neighborNames.length,
    topNeighbors: neighborNames.slice(0, maxNeighbors),
  };
}
